import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Trash2 } from "lucide-react";
import {
  type WhatsAppTemplateCategory,
  type WhatsAppTemplateWriteResult,
} from "@/lib/whatsapp-cloud-templates";
import {
  buildTemplateComponents,
  metaToDisplay,
  substituteVariablesForPreview,
  TEMPLATE_LIMITS,
  normalizeTemplateNameInput,
  validateTemplateForm,
  type CustomField,
} from "@/lib/whatsapp-template-form";
import { useWhatsAppCloudTemplateWrite } from "@/hooks/useWhatsAppCloudTemplateWrite";
import { useToast } from "@/hooks/use-toast";
import { WhatsAppTemplatePreviewBubble } from "@/components/integrations/WhatsAppTemplatePreviewBubble";
import { TemplateFormattingToolbar } from "@/components/integrations/TemplateFormattingToolbar";

const CATEGORY_OPTIONS: { value: WhatsAppTemplateCategory; label: string }[] = [
  { value: "UTILITY", label: "Utilidade" },
  { value: "MARKETING", label: "Marketing" },
  { value: "AUTHENTICATION", label: "Autenticação" },
];

const LANGUAGE_OPTIONS = [
  { value: "pt_BR", label: "Português (Brasil)" },
  { value: "en_US", label: "Inglês (EUA)" },
  { value: "es", label: "Espanhol" },
];

const FIELD_PRESETS = [
  { label: "Nome do cliente", example: "Maria" },
  { label: "Telefone", example: "5511999999999" },
  { label: "Número do pedido", example: "#12345" },
  { label: "Data", example: "19/08/2026" },
  { label: "Valor", example: "R$ 99,90" },
] as const;

function isTemplatesWriteDisabled(result: WhatsAppTemplateWriteResult): boolean {
  if (result.ok) return false;
  const raw = result.raw as { error?: string } | undefined;
  return raw?.error === "templates_write_disabled";
}

function insertInTextarea(
  current: string,
  before: string,
  after: string,
  placeholder: string,
  el: HTMLTextAreaElement | null,
): { next: string; cursorStart: number; cursorEnd: number } {
  const start = el?.selectionStart ?? current.length;
  const end = el?.selectionEnd ?? current.length;
  const selected = current.slice(start, end) || placeholder;
  const next = `${current.slice(0, start)}${before}${selected}${after}${current.slice(end)}`;
  const cursorStart = start + before.length;
  const cursorEnd = cursorStart + selected.length;
  return { next, cursorStart, cursorEnd };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integrationId: string;
  onCreated: () => void;
}

export function CreateWhatsAppTemplateDialog({
  open,
  onOpenChange,
  integrationId,
  onCreated,
}: Props) {
  const { toast } = useToast();
  const { writeTemplate, loading } = useWhatsAppCloudTemplateWrite();
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const [name, setName] = useState("");
  const [language, setLanguage] = useState("pt_BR");
  const [category, setCategory] = useState<WhatsAppTemplateCategory>("UTILITY");
  const [headerEnabled, setHeaderEnabled] = useState(false);
  const [headerText, setHeaderText] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [footerText, setFooterText] = useState("");
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [buttons, setButtons] = useState<string[]>([]);

  const buttonsAllowed = category === "MARKETING" || category === "UTILITY";

  const formInput = useMemo(
    () => ({
      headerEnabled,
      headerText,
      bodyText,
      footerText,
      buttons,
      customFields,
    }),
    [headerEnabled, headerText, bodyText, footerText, buttons, customFields],
  );

  const preview = useMemo(
    () => ({
      header: headerEnabled ? substituteVariablesForPreview(headerText, customFields) : undefined,
      body: substituteVariablesForPreview(bodyText, customFields),
      footer: footerText.trim() || undefined,
      buttons: buttonsAllowed ? buttons.map((b) => b.trim()).filter(Boolean) : [],
    }),
    [headerEnabled, headerText, bodyText, footerText, buttons, customFields, buttonsAllowed],
  );

  const resetForm = () => {
    setName("");
    setLanguage("pt_BR");
    setCategory("UTILITY");
    setHeaderEnabled(false);
    setHeaderText("");
    setBodyText("");
    setFooterText("");
    setCustomFields([]);
    setButtons([]);
  };

  const applyBodyEdit = (next: string, cursorStart?: number, cursorEnd?: number) => {
    setBodyText(metaToDisplay(next));
    requestAnimationFrame(() => {
      const el = bodyRef.current;
      if (!el || cursorStart === undefined || cursorEnd === undefined) return;
      el.focus();
      el.setSelectionRange(cursorStart, cursorEnd);
    });
  };

  const handleBodyInsert = (before: string, after?: string, placeholder?: string) => {
    const { next, cursorStart, cursorEnd } = insertInTextarea(
      bodyText,
      before,
      after ?? before,
      placeholder ?? "texto",
      bodyRef.current,
    );
    applyBodyEdit(next, cursorStart, cursorEnd);
  };

  const handleBodyEmoji = (emoji: string) => {
    const { next, cursorStart, cursorEnd } = insertInTextarea(bodyText, emoji, "", "", bodyRef.current);
    applyBodyEdit(next, cursorStart, cursorEnd);
  };

  const addCustomField = (presetIndex?: number) => {
    const nextIndex = customFields.length + 1;
    const preset = presetIndex !== undefined ? FIELD_PRESETS[presetIndex] : FIELD_PRESETS[0];
    setCustomFields((prev) => [
      ...prev,
      { label: preset?.label ?? "Informação", example: preset?.example ?? "" },
    ]);
    setBodyText((prev) => {
      const spacer = prev.length > 0 && !prev.endsWith(" ") ? " " : "";
      return `${prev}${spacer}[Campo ${nextIndex}]`;
    });
  };

  const removeCustomField = (index: number) => {
    const varNum = index + 1;
    setCustomFields((prev) => prev.filter((_, i) => i !== index));
    setBodyText((prev) => {
      let next = prev.replace(new RegExp(`\\[Campo\\s+${varNum}\\]`, "gi"), "");
      for (let i = index + 2; i <= customFields.length; i++) {
        next = next.replace(new RegExp(`\\[Campo\\s+${i}\\]`, "gi"), `[Campo ${i - 1}]`);
      }
      return next.replace(/\s{2,}/g, " ").trim();
    });
  };

  const addButton = () => {
    if (buttons.length >= TEMPLATE_LIMITS.maxButtons) return;
    setButtons((prev) => [...prev, ""]);
  };

  const updateButton = (index: number, value: string) => {
    setButtons((prev) => prev.map((b, i) => (i === index ? value : b)));
  };

  const removeButton = (index: number) => {
    setButtons((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (category === "AUTHENTICATION") {
      toast({
        title: "Categoria indisponível",
        description:
          "Templates de autenticação exigem botões OTP no formato da Meta. Use Marketing ou Utilidade por enquanto.",
        variant: "destructive",
      });
      return;
    }

    const validation = validateTemplateForm(name, formInput, { buttonsAllowed });
    if (!validation.ok) {
      toast({ title: validation.title, description: validation.description, variant: "destructive" });
      return;
    }

    const result = await writeTemplate({
      integration_id: integrationId,
      action: "create",
      name: name.trim().toLowerCase(),
      language,
      category,
      components: buildTemplateComponents(formInput),
      sync_after: true,
    });

    if (!result.ok) {
      toast({
        title: isTemplatesWriteDisabled(result) ? "Criação indisponível" : "Falha ao criar template",
        description: isTemplatesWriteDisabled(result)
          ? "A criação de templates está temporariamente indisponível. Tente sincronizar templates existentes."
          : result.error,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Template enviado à Meta",
      description: (() => {
        const status = String(result.graph?.status ?? "PENDING").toUpperCase();
        if (status === "REJECTED") {
          return "O template foi rejeitado pela Meta. Veja o motivo na lista ou sincronize.";
        }
        if (status === "APPROVED") {
          return "O template foi aprovado pela Meta.";
        }
        return "O template foi criado e está aguardando revisão (status PENDING).";
      })(),
    });
    resetForm();
    onOpenChange(false);
    onCreated();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetForm();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Criar mensagem modelo</DialogTitle>
          <DialogDescription>
            Monte a mensagem como no WhatsApp. Na hora de enviar, os campos personalizados serão preenchidos
            com o nome e dados de cada contato.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Nome interno</Label>
              <Input
                placeholder="ex.: confirmacao de pedido"
                value={name}
                onChange={(e) => setName(normalizeTemplateNameInput(e.target.value))}
              />
              <p className="text-xs text-muted-foreground">
                Só para organização — não aparece para o cliente. Espaços viram underscore automaticamente.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Idioma</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Categoria</Label>
                <Select
                  value={category}
                  onValueChange={(v) => {
                    const next = v as WhatsAppTemplateCategory;
                    setCategory(next);
                    if (next === "AUTHENTICATION") setButtons([]);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2 rounded-lg border p-3">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="header-toggle">Cabeçalho (opcional)</Label>
                <Switch
                  id="header-toggle"
                  checked={headerEnabled}
                  onCheckedChange={(checked) => {
                    setHeaderEnabled(checked);
                    if (!checked) setHeaderText("");
                  }}
                />
              </div>
              {headerEnabled && (
                <div className="space-y-1">
                  <Input
                    placeholder="Título do template"
                    value={headerText}
                    onChange={(e) => setHeaderText(metaToDisplay(e.target.value))}
                    maxLength={TEMPLATE_LIMITS.headerText}
                  />
                  <p className="text-xs text-muted-foreground">
                    Texto curto no topo da mensagem (máx. {TEMPLATE_LIMITS.headerText} caracteres).
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Corpo</Label>
              <TemplateFormattingToolbar onInsert={handleBodyInsert} onEmoji={handleBodyEmoji} />
              <Textarea
                ref={bodyRef}
                placeholder="Olá! Seu pedido foi confirmado. Use «Adicionar personalização» para incluir o nome do cliente."
                value={bodyText}
                onChange={(e) => setBodyText(metaToDisplay(e.target.value))}
                rows={5}
              />
              <p className="text-xs text-muted-foreground">
                Formate com negrito e emojis. Personalizações não podem ficar no início nem no fim da mensagem.
              </p>
            </div>

            <div className="space-y-1">
              <Label>Rodapé (opcional)</Label>
              <Input
                placeholder="Texto pequeno no final da bolha"
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                maxLength={TEMPLATE_LIMITS.footerText}
              />
              <p className="text-xs text-muted-foreground">
                Máx. {TEMPLATE_LIMITS.footerText} caracteres, sem formatação.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label>Personalizações</Label>
                <div className="flex flex-wrap gap-1">
                  {FIELD_PRESETS.slice(0, 3).map((preset, idx) => (
                    <Button
                      key={preset.label}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => addCustomField(idx)}
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>
              {customFields.length === 0 ? (
                <p className="text-xs text-muted-foreground rounded border border-dashed p-3">
                  Adicione personalizações para incluir o nome do cliente, número do pedido ou outras
                  informações que mudam em cada envio.
                </p>
              ) : (
                customFields.map((field, idx) => (
                  <div key={idx} className="space-y-2 rounded border p-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-foreground">
                        {field.label.trim() || `Personalização ${idx + 1}`}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => removeCustomField(idx)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <Input
                      placeholder="O que é este dado? (ex.: Nome do cliente)"
                      value={field.label}
                      onChange={(e) =>
                        setCustomFields((prev) =>
                          prev.map((f, i) => (i === idx ? { ...f, label: e.target.value } : f)),
                        )
                      }
                    />
                    <Input
                      placeholder="Exemplo para aprovação (ex.: Maria)"
                      value={field.example}
                      onChange={(e) =>
                        setCustomFields((prev) =>
                          prev.map((f, i) => (i === idx ? { ...f, example: e.target.value } : f)),
                        )
                      }
                    />
                  </div>
                ))
              )}
            </div>

            {buttonsAllowed && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Botões (opcional)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addButton}
                    disabled={buttons.length >= TEMPLATE_LIMITS.maxButtons}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Adicionar botão
                  </Button>
                </div>
                {buttons.length === 0 ? (
                  <p className="text-xs text-muted-foreground rounded border border-dashed p-3">
                    Até {TEMPLATE_LIMITS.maxButtons} respostas rápidas (máx. {TEMPLATE_LIMITS.buttonText} caracteres
                    cada).
                  </p>
                ) : (
                  buttons.map((btn, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        placeholder={`Botão ${idx + 1}`}
                        value={btn}
                        onChange={(e) => updateButton(idx, e.target.value)}
                        maxLength={TEMPLATE_LIMITS.buttonText}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => removeButton(idx)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="space-y-2 md:sticky md:top-0 md:self-start">
            <Label className="text-xs text-muted-foreground">Pré-visualização no WhatsApp</Label>
            <div className="rounded-xl bg-[#0b141a] p-4 min-h-[240px]">
              <WhatsAppTemplatePreviewBubble
                headerText={preview.header}
                bodyText={preview.body}
                footerText={preview.footer}
                buttons={preview.buttons}
              />
            </div>
            {customFields.length > 0 && (
              <p className="text-xs text-muted-foreground">
                No preview, os exemplos mostram como a mensagem ficará. Ao enviar no chat, cada contato recebe
                seus próprios dados.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Criar e enviar para aprovação
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
